import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, TargetIcon, UsersIcon, MessageSquareIcon, BookOpenIcon, LogOutIcon, UserIcon, SettingsIcon, ShieldCheckIcon, KeyIcon, HelpCircleIcon, SwitchCameraIcon, XIcon, CheckIcon, BellIcon, TrophyIcon, UserPlusIcon } from 'lucide-react';
interface LayoutProps {
  children: React.ReactNode;
  userType: 'mentor' | 'mentee' | null;
  onLogout: () => void;
}
interface Notification {
  id: number | string;
  type: string;
  title: string;
  description: string;
  date: string;
  read?: boolean;
  action?: string;
  actionLink?: string;
}
const Layout: React.FC<LayoutProps> = ({
  children,
  userType,
  onLogout
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([...(userType === 'mentee' ? [{
    id: 'accountability',
    type: 'info',
    title: 'Want help staying accountable?',
    description: 'Add a support buddy to help you stay on track.',
    action: 'Add Buddy',
    actionLink: '/settings',
    date: 'Now',
    read: false
  }] : []), {
    id: 'verification',
    type: 'warning',
    title: 'Verification Required',
    description: `Complete ${userType} verification to unlock all features`,
    action: 'Verify Now',
    actionLink: '/verify',
    date: 'Now',
    read: false
  }, {
    id: 1,
    type: 'update',
    title: 'Next Session Scheduled',
    description: 'Your next mentorship session is tomorrow at 3:00 PM',
    date: '1 hour ago',
    read: false
  }, {
    id: 2,
    type: 'achievement',
    title: 'Goal Milestone Reached',
    description: "You're 75% complete with your UX Portfolio goal",
    date: '2 hours ago',
    read: false
  }]);
  const getUnreadCount = () => notifications.filter(n => !n.read).length;
  const markNotificationAsRead = (id: number | string) => {
    setNotifications(notifications.map(notif => notif.id === id ? {
      ...notif,
      read: true
    } : notif));
  };
  const handleUserTypeChange = (type: 'mentor' | 'mentee') => {
    // Save the current user type to localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      // Store the current profile data
      localStorage.setItem(`${parsedData.userType}Data`, JSON.stringify(parsedData));
      // Check if we have data for the target user type
      const targetUserData = localStorage.getItem(`${type}Data`);
      if (targetUserData) {
        localStorage.setItem('userData', targetUserData);
      } else {
        // Create new data for the target user type
        localStorage.setItem('userData', JSON.stringify({
          ...parsedData,
          userType: type,
          profile: {
            ...parsedData.profile,
            mentorshipCategories: [] // Reset categories
          }
        }));
      }
      // Reload the page to apply changes
      window.location.href = type === 'mentor' ? '/mentor-dashboard' : '/mentee-dashboard';
    }
  };
  const handleNavigation = (path: string, implemented: boolean = true) => {
    if (implemented) {
      navigate(path);
    } else {
      navigate('/work-in-progress');
    }
  };
  const NotificationDropdown = () => <div className="absolute right-0 mt-2 w-80 bg-white bg-opacity-80 backdrop-blur rounded-xl shadow-glass overflow-hidden z-50 border border-purple-100" data-id="element-138">
      <div className="p-3 border-b border-purple-100" data-id="element-139">
        <h3 className="font-semibold text-purple-900" data-id="element-140">Notifications</h3>
      </div>
      <div className="max-h-96 overflow-y-auto" data-id="element-141">
        {notifications.map(notification => <div key={notification.id} className={`p-3 border-b border-purple-100 hover:bg-purple-50 cursor-pointer ${!notification.read ? 'bg-purple-50' : ''}`} onClick={() => {
        markNotificationAsRead(notification.id);
        if (notification.actionLink) {
          // Handle navigation if needed
        }
      }} data-id="element-142">
            <div className="flex items-start" data-id="element-143">
              <div className="flex-1" data-id="element-144">
                <p className="text-sm font-medium text-purple-900" data-id="element-145">
                  {notification.title}
                </p>
                <p className="text-xs text-purple-700" data-id="element-146">
                  {notification.description}
                </p>
                <p className="text-xs text-purple-400 mt-1" data-id="element-147">
                  {notification.date}
                </p>
              </div>
              {!notification.read && <div className="w-2 h-2 bg-purple-600 rounded-full mt-1" data-id="element-148"></div>}
            </div>
          </div>)}
      </div>
    </div>;
  const getNavItems = () => {
    const hasActiveMentorship = false;
    const baseItems = [{
      path: userType === 'mentor' ? '/mentor-dashboard' : '/mentee-dashboard',
      label: 'Home',
      icon: <HomeIcon size={20} data-id="element-149" />
    }, {
      path: '/mentor-hub/goals',
      label: 'Mentor Hub',
      icon: <TargetIcon size={20} data-id="element-150" />
    }, {
      path: '/resources',
      label: 'Resources',
      icon: <BookOpenIcon size={20} data-id="element-151" />
    }];
    return baseItems;
  };
  const navItems = getNavItems();
  const ProfileMenu = () => <div className="absolute right-0 top-14 mt-2 w-48 rounded-xl shadow-glass bg-white bg-opacity-80 backdrop-blur border border-purple-100" data-id="element-152">
      <div className="py-1" role="menu" data-id="element-153">
        <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" onClick={() => setShowProfileMenu(false)} data-id="element-154">
          <UserIcon size={16} className="mr-3 text-purple-500" data-id="element-155" />
          Profile
        </Link>
        <Link to="/trophies" className="flex items-center px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" onClick={() => setShowProfileMenu(false)} data-id="element-156">
          <TrophyIcon size={16} className="mr-3 text-purple-500" data-id="element-157" />
          Trophies
        </Link>
        {userType === 'mentee' && <Link to="/settings/support-buddy" className="flex items-center px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" onClick={() => setShowProfileMenu(false)} data-id="element-158">
            <UserPlusIcon size={16} className="mr-3 text-purple-500" data-id="element-159" />
            Support Buddy
          </Link>}
        <button onClick={() => {
        setShowProfileMenu(false);
        handleNavigation('/settings', false);
      }} className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" data-id="element-160">
          <SettingsIcon size={16} className="mr-3 text-purple-500" data-id="element-161" />
          Settings
        </button>
        <Link to="/verify" className="flex items-center px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" onClick={() => setShowProfileMenu(false)} data-id="element-162">
          <ShieldCheckIcon size={16} className="mr-3 text-purple-500" data-id="element-163" />
          Verify Account
        </Link>
        <button onClick={() => {
        setShowProfileMenu(false);
        handleNavigation('/security', false);
      }} className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" data-id="element-164">
          <KeyIcon size={16} className="mr-3 text-purple-500" data-id="element-165" />
          Security
        </button>
        <button onClick={() => {
        setShowProfileMenu(false);
        handleNavigation('/help', false);
      }} className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" data-id="element-166">
          <HelpCircleIcon size={16} className="mr-3 text-purple-500" data-id="element-167" />
          Help & Support
        </button>
        <button onClick={onLogout} className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50" role="menuitem" data-id="element-168">
          <LogOutIcon size={16} className="mr-3 text-purple-500" data-id="element-169" />
          Logout
        </button>
      </div>
    </div>;
  return <div className="flex flex-col min-h-screen bg-background" data-id="element-170">
      <header className="bg-white bg-opacity-80 backdrop-blur fixed top-0 left-0 right-0 z-50 border-b border-purple-100" data-id="element-171">
        <div className="max-w-7xl mx-auto px-4" data-id="element-172">
          <div className="flex justify-between items-center h-16" data-id="element-173">
            <h1 className="text-xl font-bold text-purple-700" data-id="element-174">PrideMentor</h1>
            <div className="flex items-center space-x-4" data-id="element-175">
              <div className="relative" data-id="element-176">
                <button className="p-2 rounded-full hover:bg-purple-50 relative" onClick={() => setShowNotifications(!showNotifications)} data-id="element-177">
                  <BellIcon size={20} className="text-purple-700" data-id="element-178" />
                  {getUnreadCount() > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center" data-id="element-179">
                      {getUnreadCount()}
                    </span>}
                </button>
                {showNotifications && <NotificationDropdown data-id="element-180" />}
              </div>
              <div className="relative" data-id="element-181">
                <button className="flex items-center space-x-2 text-sm focus:outline-none" onClick={() => setShowProfileMenu(!showProfileMenu)} data-id="element-182">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-183">
                    <UserIcon size={20} className="text-purple-600" data-id="element-184" />
                  </div>
                </button>
                {showProfileMenu && <ProfileMenu data-id="element-185" />}
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 pt-16 pb-16" data-id="element-186">
        <div className="max-w-7xl mx-auto px-4" data-id="element-187">{children}</div>
      </main>
      <nav className="fixed bottom-0 w-full bg-white bg-opacity-80 backdrop-blur border-t border-purple-100" data-id="element-188">
        <div className="max-w-7xl mx-auto px-4" data-id="element-189">
          <div className="flex justify-around" data-id="element-190">
            {navItems.map(item => <Link key={item.path} to={item.path} className={`flex flex-col items-center py-2 px-3 ${location.pathname === item.path ? 'text-purple-600' : 'text-purple-400 hover:text-purple-700'}`} data-id="element-191">
                {item.icon}
                <span className="text-xs mt-1" data-id="element-192">{item.label}</span>
              </Link>)}
          </div>
        </div>
      </nav>
    </div>;
};
export default Layout;